import React, { useEffect } from 'react';
import PageLayout from '../components/templates/PageLayout';
import LinkButton from '../components/atoms/LinkButton';
import LazyImage from '../components/atoms/LazyImage';
import ResponsiveLazyImage from '../components/atoms/ResponsiveLazyImage';
import MethodologySection from '../components/organisms/MethodologySection';
import PlayTrackBand from '../components/organisms/PlayTrackBand';
import YoutubeEmbed from '../components/molecules/YoutubeEmbed';
import { activeOffer } from '../data/activeOffer';
import { facts } from '../data/facts';
import { homeMethodology } from '../data/methodology';

const FootyLab = () => {
  useEffect(() => {
    document.title = 'footyLab | DataRook';

    const link = document.querySelector("link[rel~='icon']");
    if (link) {
      link.href = '/footylab_v2_icon.png';
    }
    return () => {
      if (link) {
        link.href = '/dr_icon.png';
      }
    };
  }, []);

  return (
    <PageLayout>
      <div className="dr-container pt-16">
        <h1 className="text-4xl font-bold mb-8 text-center">
          footyLab: Where Soccer Meets Data Science
        </h1>
        <div className="mb-12 text-center">
          <LazyImage
            src="/footylab_v2_vertical.png"
            alt="footyLab Logo"
            className="mx-auto mb-8 max-w-[360px] max-h-[200px] w-auto h-auto object-contain"
            style={{ maxWidth: '360px', maxHeight: '200px' }}
            loading="eager"
            fetchPriority="high"
          />
          <p className="text-xl mb-6">
            footyLab is a groundbreaking program that combines the excitement of soccer with
            cutting-edge data science education. Using professional-grade analytics tools,{' '}
            {facts.ageSentence.toLowerCase()} Students learn to analyze their own performance
            data, developing crucial STEM skills along the way.
          </p>
        </div>
      </div>

      <PlayTrackBand />

      <div className="dr-container pb-16">
        <MethodologySection
          id="how-it-works"
          title={homeMethodology.title}
          subtitle={homeMethodology.subtitle}
          steps={homeMethodology.steps}
          className="py-0 pb-16"
        />

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6">A Story of Transformation</h2>
          <div className="bg-blue-50 p-6 border-l-4 border-blue-500 rounded-r-lg mb-8">
            <p className="text-lg italic mb-4">
              &ldquo;I hate math,&rdquo; said Dashley, a 12-year-old who loved soccer but avoided
              STEM subjects.
            </p>
            <p className="mb-4">
              A few weeks after joining footyLab, Dashley was eagerly analyzing her sprint speeds,
              comparing her data to professional athletes, and building her own performance
              dashboard. By the end of the program, she wasn&apos;t just better at soccer—she had
              transformed into a confident young data scientist with marketable skills and a new
              vision for her future.
            </p>
            <p className="font-semibold">
              &ldquo;I love data science!&rdquo; became her new mantra, as she discovered the
              power of STEM through something she already loved.
            </p>
          </div>
          <p className="text-center text-lg">
            Dashley&apos;s story isn&apos;t unique. We&apos;re seeing this transformation happen
            with students across all backgrounds and initial skill levels.
          </p>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6">Program Highlights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4">What You&apos;ll Learn</h3>
              <ul className="list-disc list-inside">
                <li>Python programming basics</li>
                <li>Data analysis and visualization</li>
                <li>Statistical concepts and their applications</li>
                <li>Soccer performance analytics</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4">What You&apos;ll Do</h3>
              <ul className="list-disc list-inside">
                <li>Play soccer using professional tracking technology</li>
                <li>Analyze your own performance data</li>
                <li>
                  Develop critical thinking and problem-solving skills by building a webapp
                </li>
                <li>Create data-driven strategies to improve your game</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-16 bg-gray-50 p-8 rounded-xl">
          <h2 className="text-3xl font-bold mb-6 text-center">
            Perfect for Every Learning Environment
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold mb-3 text-navy">🏫 Schools</h3>
              <p className="mb-4">
                <strong>Public & Private Schools:</strong> footyLab makes an excellent afterschool
                program that combines physical education with STEM learning. We align with
                curriculum standards while making learning feel like play.
              </p>
              <ul className="list-disc list-inside text-gray-700">
                <li>Engages students who might not typically join STEM clubs</li>
                <li>Provides real-world applications for math and science concepts</li>
                <li>Builds 21st-century skills through hands-on learning</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold mb-3 text-navy">🏘️ Community Organizations</h3>
              <p className="mb-4">
                <strong>Boys & Girls Clubs, YMCAs, and Community Centers:</strong> footyLab brings
                high-quality STEM education to your existing sports programs, reaching youth where
                they already gather.
              </p>
              <ul className="list-disc list-inside text-gray-700">
                <li>No expensive equipment needed - we provide everything</li>
                <li>Trained instructors guide the entire program</li>
                <li>Supports STEM interest and career awareness through hands-on learning</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md md:col-span-2">
              <h3 className="text-xl font-bold mb-3 text-navy">🏠 Homeschool Groups</h3>
              <p className="mb-4">
                <strong>Perfect for Homeschool Co-ops:</strong> footyLab offers a unique blend of
                physical education and STEM learning that&apos;s ideal for homeschool groups.{' '}
                {facts.ageSentence} With flexible scheduling and a curriculum that integrates
                multiple subject areas, it&apos;s a complete learning experience.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="list-disc list-inside text-gray-700">
                  <li>Covers PE, technology, and math standards</li>
                  <li>Encourages collaborative learning and teamwork</li>
                  <li>Adaptable to different skill levels within the program age range</li>
                </ul>
                <ul className="list-disc list-inside text-gray-700">
                  <li>Provides socialization opportunities</li>
                  <li>Real-world application of academic concepts</li>
                  <li>Parent-friendly with clear learning outcomes</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6 text-center">footyLab in Action</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="relative overflow-hidden rounded-lg shadow-lg">
              <ResponsiveLazyImage
                originalSrc="/problem_solving.png"
                alt="Students problem solving together"
                className="w-full h-48 object-cover"
                loading="lazy"
                width={800}
                height={480}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                <p className="text-white text-sm font-semibold">Collaborative Problem Solving</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-lg shadow-lg">
              <ResponsiveLazyImage
                originalSrc="/kids_coding.png"
                alt="Students coding"
                className="w-full h-48 object-cover"
                loading="lazy"
                width={800}
                height={480}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                <p className="text-white text-sm font-semibold">Learning Python Together</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-lg shadow-lg">
              <ResponsiveLazyImage
                originalSrc="/teacher_helps_student_code.png"
                alt="Teacher helping student"
                className="w-full h-48 object-cover"
                loading="lazy"
                width={800}
                height={480}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                <p className="text-white text-sm font-semibold">Personalized Instruction</p>
              </div>
            </div>
          </div>
          <h3 className="text-2xl font-semibold mb-4 text-center">Watch Our Story</h3>
          <YoutubeEmbed
            videoId="wBY0Qlk_djU"
            title="footyLab program overview video"
            sectionClassName=""
            className="aspect-w-16 aspect-h-9"
          />
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6">Our Technology</h2>
          <p className="mb-4">
            footyLab utilizes professional-grade equipment to provide a true sports analytics
            experience:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4">Veo Cameras</h3>
              <p>
                AI-powered cameras that capture 180° footage of the entire field, allowing for
                comprehensive video analysis.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4">Catapult Trackers</h3>
              <p>
                Wearable GPS devices that collect detailed performance data, including distance
                covered, speed, and acceleration.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6">Our Instructors</h2>
          <p className="mb-4">
            footyLab instructors bring a unique blend of expertise in both soccer and data science:
          </p>
          <ul className="list-disc list-inside">
            <li>Experienced youth soccer coaches</li>
            <li>Data scientists with backgrounds in sports analytics</li>
            <li>Educators passionate about making STEM accessible and engaging</li>
          </ul>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Who can participate in footyLab?</h3>
              <p>
                {facts.ageSentence} All skill levels in soccer and data science are welcome.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Do I need to bring my own equipment?</h3>
              <p>No, all necessary technology and equipment will be provided during the sessions.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">How long is the program?</h3>
              <p>
                The program typically runs for a minimum of 6-weeks or as long as you need, with
                classes held once or twice a week. We can customize the program to fit your needs.
              </p>
            </div>
          </div>
        </section>

        <div className="text-center">
          <LinkButton to={activeOffer.destination} className="text-xl py-3 px-6">
            {activeOffer.label}
          </LinkButton>
        </div>
      </div>
    </PageLayout>
  );
};

export default FootyLab;
